/**
 * Component: Klevu Landing
 * ------------------------------------------------------------------------------
 * Custom Klevu's search landing page
 *
 * @namespace klevuLanding
 */

import log from 'salvo-lite/log'

import axios from 'axios'
import Cookies from 'js-cookie'
import { formatMoney } from '@shopify/theme-currency'
import 'nouislider/distribute/nouislider.css'
import noUiSlider from 'nouislider'
import cssClasses from '../helpers/cssClasses'
import {
    on,
    formatNumber,
    getQueryParamsObject,
    percentageSaving
} from '../helpers/utils'

class KlevuLanding {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem

        /**
         * DOM this._selectors.
         */
        this._selectors = {
            klevuLandingContainer: '[js-klevu-landing="container"]',
            klevuInner: '[js-klevu-landing="inner"]',
            klevuSearchInfo: '[js-klevu-landing="info"]',
            klevuFilters: '[js-klevu-landing="filters"]',
            klevuProductGrid: '[js-klevu-landing="grid"]',
            klevuFilterModelKey: '[js-klevu-filter-key="Filter_Model"]',
            klevuFilterTitle: 'js-klevu-filter-title',
            klevuFilterLink: 'js-klevu-filter-link',
            klevuActiveFilters: '.klevu-search-landing__filter-link.is-active',
            klevuFilterKey: 'data-key',
            klevuFilterValue: 'data-value',
            klevuSortLabel: '[js-klevu-sort="label"]',
            klevuSortType: '[js-klevu-sort="type"]',
            gridCta: '[js-klevu-grid]',
            gridView: 'klevu-search-landing--grid',
            listCta: '[js-klevu-list]',
            listView: 'klevu-search-landing--list',
            priceSliderContainer: '[js-klevu-price="container"]',
            priceSlider: 'price-slider',
            minPriceRange: '[js-price-range-min]',
            maxPriceRange: '[js-price-range-max]',
            klevuPaginationContainer: '[js-klevu-landing="pagination"]',
            klevuPaginationLink: 'js-klevu-pagination',
            klevuRefineBy: '[js-klevu-landing-refine="button"]',
            klevuFilterSearch: 'js-filter-search',
            klevuFilterText: '.klevu-search-landing__filter-name',
            klevuClearFilters: 'js-klevu-clear',
            klevuAppliedFilter: 'js-applied-filter',
            klevuAppliedFiltersList: '[js-klevu-active-filters]',
            presetCarPresent: 'preset-car',
            noResults: 'no-results',
            klevuNoResults: '[js-klevu-landing="empty"]',
            klevuProductLink: 'js-klevu-product-link',
            klevuCountWrapper: '[js-klevu-landing="count-wrapper"]',
            klevuFuzzyMessage: '[js-klevu-landing="fuzzy"]'
        }

        /**
         * Global variables.
         */
        this._klevuSearchEndpoint = window.theme.klevuSearchEndpoint
        this._klevuSearchApiKey = window.theme.klevuSearchApi
        this._urlParameters = getQueryParamsObject()
        this._incomingSearchTerm = this._urlParameters.q.split('+').join(' ').replace(/[^\w ]/g, '')
        this._totalProductsPerPage = 36
        this._activeFiltersArray = []
        this._presetFilterArray = []
        this._excludedCategoriesArray = []
        this._excludedCategoriesList = window.theme.searchLandingHiddenFilters.list.split(',')
        this._excludedCategoriesList.forEach((item) => this._excludedCategoriesArray.push(item))

        /**
         * DOM node selectors.
         */
        this._nodeSelectors = {
            klevuLandingContainer: document.querySelector(this._selectors.klevuLandingContainer),
            klevuInner: document.querySelector(this._selectors.klevuInner),
            klevuSearchInfo: [...document.querySelectorAll(this._selectors.klevuSearchInfo)],
            klevuFilters: document.querySelector(this._selectors.klevuFilters),
            klevuProductGrid: document.querySelector(this._selectors.klevuProductGrid),
            klevuSortLabel: document.querySelector(this._selectors.klevuSortLabel),
            klevuSortType: [...document.querySelectorAll(this._selectors.klevuSortType)],
            gridCta: document.querySelector(this._selectors.gridCta),
            listCta: document.querySelector(this._selectors.listCta),
            priceSliderContainer: document.querySelector(this._selectors.priceSliderContainer),
            priceSlider: document.getElementById(this._selectors.priceSlider),
            minPriceRange: document.querySelector(this._selectors.minPriceRange),
            maxPriceRange: document.querySelector(this._selectors.maxPriceRange),
            klevuPaginationContainer: document.querySelector(this._selectors.klevuPaginationContainer),
            klevuRefineBy: document.querySelector(this._selectors.klevuRefineBy),
            klevuAppliedFiltersList: document.querySelector(this._selectors.klevuAppliedFiltersList),
            klevuNoResults: document.querySelector(this._selectors.klevuNoResults),
            klevuFuzzyMessage: document.querySelector(this._selectors.klevuFuzzyMessage),
            klevuCountWrapper: document.querySelector(this._selectors.klevuCountWrapper)
        }
        log.debug(this.constructor.name, 'Constructed', this)
    }

    onInit () {
        this._setInitState()
        this._setEventListeners()
    }

    /**
     * Set init sate.
     */
    _setInitState () {
        let currentCar = Cookies.get('Current_Car')
        const initPriceSlider = true

        if (currentCar) {
            currentCar = JSON.parse(currentCar)
            this._nodeSelectors.klevuLandingContainer.classList.add(this._selectors.presetCarPresent)
            this._combinePresetCarModel(currentCar)
            this._runPresetCarSearch(initPriceSlider)
        } else {
            this._runInitSearch(initPriceSlider)
        }
    }

    /**
     * Set event listeners.
     */
    _setEventListeners () {
        on('click', document, (event) => {
            if (event.target.hasAttribute(this._selectors.klevuFilterTitle)) {
                event.target.classList.toggle(cssClasses.collapsed)
            } else if (event.target.hasAttribute(this._selectors.klevuFilterLink)) {
                const filterType = 'filter'
                let includePriceCheck
                event.target.classList.toggle(cssClasses.active)

                if (this._activeFiltersArray.findIndex((item) => item.key === 'Price') > -1) {
                    includePriceCheck = true
                } else {
                    includePriceCheck = false
                }

                if (this._activeFiltersArray.findIndex((item) => item.value === event.target.dataset.value) > -1) {
                    const filtered = this._activeFiltersArray.filter((el) => el.value !== event.target.dataset.value)
                    this._activeFiltersArray = filtered
                } else {
                    this._activeFiltersArray.push({
                        key: event.target.dataset.key,
                        value: event.target.dataset.value
                    })
                }

                this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), this._checkCurrentSorting(), 1, filterType)
            } else if (event.target.hasAttribute(this._selectors.klevuAppliedFilter)) {
                const filterType = 'filter'
                const includePriceCheck = false
                const appliedFilter = this._nodeSelectors.klevuFilters.querySelector(`[data-value='${event.target.dataset.value}']`)
                appliedFilter.classList.remove(cssClasses.active)

                if (this._activeFiltersArray.findIndex((item) => item.value === event.target.dataset.value) > -1) {
                    const filtered = this._activeFiltersArray.filter((el) => el.value !== event.target.dataset.value)
                    this._activeFiltersArray = filtered
                } else {
                    this._activeFiltersArray.push({
                        key: event.target.dataset.key,
                        value: event.target.dataset.value
                    })
                }

                this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), this._checkCurrentSorting(), 1, filterType)
            } else if (event.target.hasAttribute(this._selectors.klevuClearFilters)) {
                const initPriceSlider = false
                this._activeFiltersArray = []
                this._nodeSelectors.klevuAppliedFiltersList.innerHTML = ''
                this._runInitSearch(initPriceSlider)
            } else if (event.target.hasAttribute(this._selectors.klevuPaginationLink)) {
                const filterType = 'pagination'
                const includePriceCheck = false
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })

                this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), this._checkCurrentSorting(), Number(event.target.dataset.page), filterType)
            } else if (event.target.hasAttribute(this._selectors.klevuProductLink)) {
                event.preventDefault()
                this._clickedRecordAnalytics(event.target)
            }
        })

        on('keyup', document, (event) => {
            if (event.target.hasAttribute(this._selectors.klevuFilterSearch)) {
                this._searchFilterOptions(event.target.value, event.target.dataset.key)
            }
        })

        on('click', this._nodeSelectors.gridCta, () => this._activateNewGrid(this._selectors.listView, this._selectors.gridView))
        on('click', this._nodeSelectors.listCta, () => this._activateNewGrid(this._selectors.gridView, this._selectors.listView))
        on('click', this._nodeSelectors.klevuRefineBy, (event) => this._filterToggle(event))
        on('click', this._nodeSelectors.klevuSortLabel, (event) => event.currentTarget.classList.toggle(cssClasses.active))

        this._nodeSelectors.klevuSortType.forEach((item) => {
            const filterType = 'sort'
            const includePriceCheck = false

            item.addEventListener('click', () => {
                this._nodeSelectors.klevuSortLabel.innerHTML = item.innerHTML
                this._nodeSelectors.klevuSortLabel.dataset.value = item.dataset.value
                this._nodeSelectors.klevuSortLabel.classList.remove(cssClasses.active)
                this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), item.dataset.value, 1, filterType)
            })
        })

        window.Frame.EventBus.listen('CurrentCar:setCar', (currentCar) => {
            this._presetFilterArray = []
            const initPriceSlider = false
            this._activeFiltersArray = []
            this._combinePresetCarModel(currentCar)
            this._nodeSelectors.klevuLandingContainer.classList.add(this._selectors.presetCarPresent)
            this._nodeSelectors.klevuAppliedFiltersList.innerHTML = ''
            this._runPresetCarSearch(initPriceSlider)
        })

        window.Frame.EventBus.listen('CurrentCar:clear', () => {
            this._presetFilterArray = []
            const filterType = 'filter'
            const includePriceCheck = false
            this._activeFiltersArray = this._activeFiltersArray.filter((el) => el.key !== 'Filter_Model' && el.key !== 'Filter_VehicleCode')
            this._nodeSelectors.klevuLandingContainer.classList.remove(this._selectors.presetCarPresent)
            this._nodeSelectors.klevuAppliedFiltersList.innerHTML = ''
            this._deactivateAllCarModels()
            this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), this._checkCurrentSorting(), 1, filterType)
        })
    }

    /**
     * Search within filter options.
     * @param {Object} value - Search term.
     * @param {Object} target - Filter key.
     */
    _searchFilterOptions (value, target) {
        const filterOptions = this._nodeSelectors.klevuFilters.querySelectorAll(`li[data-key='${target}']`)

        filterOptions.forEach((filter) => {
            const filterLink = filter.querySelector(this._selectors.klevuFilterText)

            if (filterLink.innerText.toUpperCase().indexOf(value.toUpperCase()) > -1) {
                filter.classList.remove(cssClasses.hidden)
            } else {
                filter.classList.add(cssClasses.hidden)
            }
        })
    }

    /**
     * Combine preset car model.
     * @param {Object} currentCar - Parsed car cookie object.
     */
    _combinePresetCarModel (currentCar) {
        if (currentCar.year) {
            const presentYearString = ` - ${window.theme.strings.currentCar.presentYear}`
            const carModel = currentCar.model.replace(/ /g, '-')
            const yearFormatted = `_${currentCar.year.replace(presentYearString, '').replace(/ /g, '')}`
            let currentCarTag = carModel + yearFormatted
            currentCarTag = currentCarTag.replace('Ecosport', 'EcoSport').replace('Ka+', 'KA+')

            this._presetFilterArray.push({
                key: 'Filter_Model',
                values: ['All_Model', currentCarTag]
            })
        } else {
            this._presetFilterArray.push({
                key: 'Filter_VehicleCode',
                values: ['All_VehicleCode', currentCar.vehicleCode]
            })
        }
    }

    /**
     * Runs a search query for the preset car model.
     * @param {String} currentCar - Current car model.
     * @param {Boolean} initPriceSlider - Indicates if price range slider needs to initiate.
     */
    _runPresetCarSearch (initPriceSlider) {
        axios({
            method: 'post',
            url: this._klevuSearchEndpoint,
            data: {
                context: {
                    apiKeys: [
                        this._klevuSearchApiKey
                    ]
                },
                recordQueries: [{
                    id: 'productSearch',
                    typeOfRequest: 'SEARCH',
                    settings: {
                        query: {
                            term: this._incomingSearchTerm
                        },
                        limit: 200,
                        typeOfRecords: ['KLEVU_PRODUCT'],
                        fields: ['id', 'startPrice', 'price', 'sku', 'url', 'image', 'name', 'salePrice', 'tags'],
                        typeOfSearch: 'DEFAULT',
                        sort: 'RELEVANCE'
                    },
                    filters: {
                        filtersToReturn: {
                            enabled: true,
                            rangeFilterSettings: [{
                                key: 'klevu_price',
                                minMax: true
                            } ],
                            options: {
                                limit: 80
                            }
                        },
                        applyFilters: {
                            filters: this._presetFilterArray
                        }
                    }
                }]
            }
        })
            .then(({
                data
            }) => {
                if (data.queryResults[0].meta.totalResultsFound === 0) {
                    this._activateNoResults(data)
                } else {
                    if (data.queryResults[0].meta.typeOfSearch !== 'WILDCARD_AND') {
                        this._showFuzzyResultsMessage()
                    } else {
                        this._deactivateNoResults()
                    }

                    if (initPriceSlider) {
                        if (this._checkForEqualPriceValues(data.queryResults[0].filters)) {
                            this._nodeSelectors.priceSlider.setAttribute('disabled', true)
                            this._nodeSelectors.priceSliderContainer.classList.add(cssClasses.hidden)
                        } else {
                            this._constructPriceSlider(data.queryResults[0].filters)
                            this._nodeSelectors.priceSlider.removeAttribute('disabled')
                            this._nodeSelectors.priceSliderContainer.classList.remove(cssClasses.hidden)
                        }
                    } else {
                        this._renderConditionalPriceSlider(data.queryResults[0].filters)
                    }

                    const firstPageResults = data.queryResults[0].records.slice(0, this._totalProductsPerPage)
                    this._buildPagination(1, data.queryResults[0].records.length)
                    this._updateTotalProductCount(data.queryResults[0].meta.totalResultsFound)
                    this._nodeSelectors.klevuFilters.innerHTML = this._renderSearchFilters(data.queryResults[0].filters)
                    this._nodeSelectors.klevuProductGrid.innerHTML = this._renderSearchResults(firstPageResults)

                    this._activateCurrentModel(this._presetFilterArray[0].values[0])
                    this._activeFiltersArray.push({
                        key: this._presetFilterArray[0].key,
                        value: this._presetFilterArray[0].values[0]
                    })
                    this._constructAppliedFiltersList()

                    this._searchedTermAnalytics(data)
                }

                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
     * Display message to user telling them there is no exact match was found
     */
    _showFuzzyResultsMessage () {
        this._nodeSelectors.klevuCountWrapper.classList.add(cssClasses.hidden)
        let text = this._nodeSelectors.klevuFuzzyMessage.innerText
        text = text.replace('#', '"' + this._incomingSearchTerm + '"')
        this._nodeSelectors.klevuFuzzyMessage.innerText = text
        this._nodeSelectors.klevuFuzzyMessage.classList.remove(cssClasses.hidden)
    }

    /**
     * Checks for initialised price slider.
     * @param {String} data - Search filter data.
     */
    _renderConditionalPriceSlider (data) {
        if (this._nodeSelectors.priceSlider.innerHTML === '') {
            this._nodeSelectors.priceSlider.removeAttribute('disabled')
            this._nodeSelectors.priceSliderContainer.classList.remove(cssClasses.hidden)
            this._constructPriceSlider(data)
        } else {
            this._updateConditionalPriceSlider(data)
        }
    }

    /**
     * Updates existing price slider and amends it's attributes.
     * @param {String} data - Search filter data.
     */
    _updateConditionalPriceSlider (data) {
        if (this._checkForEqualPriceValues(data)) {
            this._nodeSelectors.priceSlider.setAttribute('disabled', true)
            this._nodeSelectors.priceSliderContainer.classList.add(cssClasses.hidden)
        } else {
            this._nodeSelectors.priceSlider.removeAttribute('disabled')
            this._nodeSelectors.priceSliderContainer.classList.remove(cssClasses.hidden)
            this._updateFilteredPriceRange(data)
            this._updatePriceSliderValues()
        }
    }

    /**
     * Activates current car model inside the filter tree.
     * @param {String} currentCar - Current car model.
     */
    _activateCurrentModel (currentCar) {
        const activeCar = this._nodeSelectors.klevuFilters.querySelector(`[data-value="${currentCar}"]`)
        activeCar.classList.add(cssClasses.active)
    }

    /**
     * Deactivates all preselected car models.
     */
    _deactivateAllCarModels () {
        const allCarModels = this._nodeSelectors.klevuFilters.querySelectorAll('button[data-key="Filter_Model"]')
        const allVehicleCodes = this._nodeSelectors.klevuFilters.querySelectorAll('button[data-key="Filter_VehicleCode"]')

        allCarModels.forEach((car) => {
            car.classList.remove(cssClasses.active)
        })

        allVehicleCodes.forEach((car) => {
            car.classList.remove(cssClasses.active)
        })
    }

    /**
     * Activates no results template.
     * @param {Object} data - Search query data.
     */
    _activateNoResults (data) {
        this._nodeSelectors.klevuFilters.innerHTML = ''
        this._nodeSelectors.klevuLandingContainer.classList.add(this._selectors.noResults)
        this._updateTotalProductCount(data.queryResults[0].meta.totalResultsFound)
        this._nodeSelectors.klevuProductGrid.innerHTML = this._renderNoSearchResultsTemplate(this._incomingSearchTerm)
    }

    /**
     * Activates no results template.
     */
    _deactivateNoResults () {
        this._nodeSelectors.klevuLandingContainer.classList.remove(this._selectors.noResults)
        this._nodeSelectors.klevuProductGrid.innerHTML = ''
    }

    /**
     * Runs initial search query if current car is not set.
     * @param {Boolean} - Indicates if price range slider needs to initiate.
     */
    _runInitSearch (initPriceSlider) {
        axios({
            method: 'post',
            url: this._klevuSearchEndpoint,
            data: {
                context: {
                    apiKeys: [
                        this._klevuSearchApiKey
                    ]
                },
                recordQueries: [{
                    id: 'productSearch',
                    typeOfRequest: 'SEARCH',
                    settings: {
                        query: {
                            term: this._incomingSearchTerm
                        },
                        limit: 200,
                        typeOfRecords: ['KLEVU_PRODUCT'],
                        fields: ['id', 'startPrice', 'price', 'sku', 'url', 'image', 'name', 'salePrice', 'tags'],
                        typeOfSearch: 'DEFAULT',
                        sort: 'RELEVANCE'
                    },
                    filters: {
                        filtersToReturn: {
                            enabled: true,
                            rangeFilterSettings: [{
                                key: 'klevu_price',
                                minMax: true
                            } ],
                            options: {
                                limit: 80
                            }
                        }
                    }
                } ]
            }
        })
            .then(({
                data
            }) => {
                if (data.queryResults[0].meta.totalResultsFound === 0) {
                    this._nodeSelectors.klevuLandingContainer.classList.add('no-init-results')
                    this._updateTotalProductCount(data.queryResults[0].meta.totalResultsFound)
                    this._nodeSelectors.klevuNoResults.innerHTML = this._renderNoSearchResultsTemplate(this._incomingSearchTerm)
                } else {
                    this._nodeSelectors.klevuLandingContainer.classList.remove('no-init-results')

                    if (data.queryResults[0].meta.typeOfSearch !== 'WILDCARD_AND') {
                        this._showFuzzyResultsMessage()
                    }

                    if (initPriceSlider) {
                        if (this._checkForEqualPriceValues(data.queryResults[0].filters)) {
                            this._nodeSelectors.priceSlider.setAttribute('disabled', true)
                            this._nodeSelectors.priceSliderContainer.classList.add(cssClasses.hidden)
                        } else {
                            this._constructPriceSlider(data.queryResults[0].filters)
                            this._nodeSelectors.priceSlider.removeAttribute('disabled')
                            this._nodeSelectors.priceSliderContainer.classList.remove(cssClasses.hidden)
                        }
                    } else {
                        this._renderConditionalPriceSlider(data.queryResults[0].filters)
                    }

                    const firstPageResults = data.queryResults[0].records.slice(0, this._totalProductsPerPage)
                    this._buildPagination(1, data.queryResults[0].records.length)
                    this._updateTotalProductCount(data.queryResults[0].meta.totalResultsFound)
                    this._nodeSelectors.klevuFilters.innerHTML = this._renderSearchFilters(data.queryResults[0].filters)
                    this._nodeSelectors.klevuProductGrid.innerHTML = this._renderSearchResults(firstPageResults)
                    this._searchedTermAnalytics(data)
                }

                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
     * Send analytics data for searched term.
     * @param {Object} searchResults - Search query data.
     */
    _searchedTermAnalytics (searchResults) {
        axios({
            method: 'post',
            url: 'https://stats.klevu.com/analytics/n-search/search',
            params: {
                klevu_apiKey: this._klevuSearchApiKey, // eslint-disable-line camelcase
                klevu_term: this._incomingSearchTerm, // eslint-disable-line camelcase
                klevu_totalResults: searchResults.queryResults[0].meta.totalResultsFound, // eslint-disable-line camelcase
                klevu_typeOfQuery: searchResults.queryResults[0].meta.typeOfSearch // eslint-disable-line camelcase
            }
        })
            .then(({
                data
            }) => {
                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
     * Send analytics data for clicked product.
     * @param {Object} product - Clicked product data.
     */
    _clickedRecordAnalytics (product) {
        axios({
            method: 'post',
            url: 'https://stats.klevu.com/analytics/productTracking',
            params: {
                klevu_apiKey: this._klevuSearchApiKey, // eslint-disable-line camelcase
                klevu_keywords: this._incomingSearchTerm, // eslint-disable-line camelcase
                klevu_type: 'clicked', // eslint-disable-line camelcase
                klevu_productId: product.dataset.id, // eslint-disable-line camelcase
                klevu_productName: product.dataset.name, // eslint-disable-line camelcase
                klevu_productUrl: product.href // eslint-disable-line camelcase
            }
        })
            .then(({
                data
            }) => {
                window.location.href = product.href

                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
     * Builds a no search results template.
     * @param {String} searchTerm - Search term.
     * @returns {String} no searh results template.
     */
    _renderNoSearchResultsTemplate (searchTerm) {
        return `
        <div class="klevu-search-landing__no-results">
          ${window.theme.strings.search.landing.noResultsFront} "${searchTerm}". ${window.theme.strings.search.landing.noResultsEnding}
        </div>
      `
    }

    /**
     * Updates total product count.
     * @param {String} count - Total search results found.
     */
    _updateTotalProductCount (count) {
        this._nodeSelectors.klevuSearchInfo.forEach((item) => {
            item.innerHTML = count
        })
    }

    /**
     * Build search results template.
     * @param {Object} records - Search query data.
     * @returns {String} search results template.
     */
    _renderSearchResults (records) {
        let html = ''
        const mobile = 'mobile'
        const desktop = 'desktop'

        records.forEach((record) => {
            html += `
          <li class="klevu-search-landing__grid-item">
            ${this._renderBadge(record, mobile)}

            <a
              class="klevu-search-landing__product"
              href="${record.url}"
              data-id="${record.id}"
              data-name="${record.name}"
              data-price="${record.salePrice}"
              js-klevu-product-link
            >
              <div class="klevu-search-landing__image-wrapper">
                <img src="${record.image}">

                ${this._renderBadge(record, desktop)}
              </div>

              <div class="klevu-search-landing__product-footer">
                <div class="klevu-search-landing__info-wrapper">
                  <div class="klevu-search-landing__product-sku">
                    <small>${window.theme.strings.partNumber} ${record.sku}</small>
                  </div>

                  <span class="klevu-search-landing__product-name">
                    ${record.name}
                  </span>
                </div>
              </div>

              <div class="klevu-search-landing__product-price">
                ${this._renderPrice(record)}
              </div>
            </a>
          </li>
        `
        })

        return html
    }

    /**
     * Build product badge.
     * @param {Object} data - Product data.
     * @returns {String}
     */
    _renderBadge (record, breakpoint) {
        let badgeText = ''
        let badgeColour = ''
        let html = ''
        const tagsArray = record.tags.replace('[', '').replace(']', '').split(', ')

        tagsArray.forEach((tag) => {
            if (tag.includes('Badge ')) {
                badgeText = tag.replace('Badge ', '')
            } else if (tag.includes('Badge_Colour ')) {
                badgeColour = tag.replace('Badge_Colour ', '')
            }
        })

        if (badgeText !== '') {
            html += `
          <span
            class="klevu-search-landing__product-badge ${breakpoint}"
            style="background-color: #${badgeColour}"
          >
            ${badgeText}
          </span>
        `
        }

        return html
    }

    /**
     * Build product price.
     * @param {Object} data - Search query data.
     * @returns {String} product price template.
     */
    _renderPrice (data) {
        let html = ''

        if (data.startPrice) {
            let penceLength = data.startPrice.split('.')
            penceLength = penceLength[1].length

            if (penceLength === 1) {
                data.startPrice += '0'
            }

            html += `
          <span>${formatMoney(data.startPrice, window.theme.moneyFormat)}</span>
        `
        } else if (data.price === data.salePrice) {
            html += `
          <span>${formatMoney(data.salePrice, window.theme.moneyFormat)}</span>
        `
        } else {
            html += `
          <span>${data.salePrice}</span>
          <s>${formatMoney(data.price, window.theme.moneyFormat)}</s>
          <span>${percentageSaving(data.salePrice, data.price)}${window.theme.strings.percentageOff}</span>
        `
        }

        return html
    }

    /**
     * Build search filters template.
     * @param {Object} filters - Search query data.
     * @returns {String} filters template.
     */
    _renderSearchFilters (filters) {
        let html = ''

        filters.forEach((filter) => {
            let inputHidden = ''

            if (filter.key === 'klevu_price') {
                return
            } else if (filter.options.length < 5) {
                inputHidden = cssClasses.hidden
            }

            html += `
          <div
            class="klevu-search-landing__filter"
            js-klevu-filter-key="${filter.key}"
          >
            <button
              class="klevu-search-landing__filter-title"
              js-klevu-filter-title
            >
              ${filter.label}
            </button>

            <input
              class="klevu-search-landing__filter-search ${inputHidden}"
              data-key="${filter.key}"
              placeholder="${window.theme.strings.search.landing.searchOptions}"
              type="text"
              js-filter-search
            >

            <ul class="klevu-search-landing__filter-list">
              ${this._renderFilterOptions(filter.key, filter.options)}
            </ul>
          </div>
        `
        })

        return html
    }

    /**
     * Renames vehicles and removes `_` from name strings.
     * @param {String} model - Model name.
     * @return {String} the renamed model.
     */
    _renameVehicle (model) {
        var modelRenamed = model

        /**
         * Create object of all names to replace.
         */
        var replacementNames = {
            '_': ' ',
            'B-Max': 'B-MAX',
            'C-Max': 'C-MAX',
            'S-Max': 'S-MAX',
            'Fiesta-Van': 'Fiesta Van',
            'Fiesta-ST': 'Fiesta ST',
            'Puma-ST': 'Puma ST',
            'Focus-ST': 'Focus ST',
            'Focus-RS': 'Focus RS',
            'Tourneo-Connect': 'Tourneo Connect',
            'Tourneo-Courier': 'Tourneo Courier',
            'Tourneo-Custom': 'Tourneo Custom',
            'Transit-Courier': 'Transit Courier',
            'Transit-Connect': 'Transit Connect',
            'Transit-Custom': 'Transit Custom',
            'Mustang-Mach-E': 'Mustang Mach-E'
        }

        /**
         * Run through object and replace.
         */
        var replaceNamesRegex = new RegExp(Object.keys(replacementNames).join('|'), 'g')
        modelRenamed = modelRenamed.replace(replaceNamesRegex, function (name) {
            return replacementNames[name]
        })

        return modelRenamed
    }

    /**
     * Build search filter options.
     * @param {String} key - Filter key.
     * @param {Object} options - Filter options.
     * @returns {String} filter options.
     */
    _renderFilterOptions (key, options) {
        let html = ''

        options.forEach((option) => {
            const optionValue = this._renameVehicle(option.name)

            if (this._excludedCategoriesArray.indexOf(optionValue) > -1 || optionValue.includes('All')) {
                return
            }

            html += `
          <li class="klevu-search-landing__filter-item" data-key="${key}">
            <button
              class="klevu-search-landing__filter-link"
              data-key="${key}"
              data-value="${option.value}"
              js-klevu-filter-link
            >
              <span class="klevu-search-landing__filter-icon"></span>

              <span class="klevu-search-landing__filter-name">
                ${optionValue}
                <span class="klevu-search-landing__filter-count">
                  (${option.count})
                </span>
              </span>
            </button>
          </li>
        `
        })

        return html
    }

    /**
     * Build a price slider.
     * @param {Object} filters - Search query data.
     */
    _constructPriceSlider (filters) {
        filters.forEach((filter) => {
            if (filter.key === 'klevu_price') {
                this._renderPriceSlider(filter.min, filter.max)
            }
        })
    }

    /**
     * Build a price slider range.
     * @param {Number} min - Min price value.
     * @param {Number} max - Max price value.
     */
    _renderPriceSlider (min, max) {
        noUiSlider.create(this._nodeSelectors.priceSlider, {
            start: [Number(min), Number(max)],
            connect: true,
            range: {
                min: Number(min),
                max: Number(max)
            },
            pips: {
                mode: 'positions',
                values: [0, 25, 50, 75, 100],
                density: 4
            }
        })

        this._updatePriceSliderValues()

        this._nodeSelectors.priceSlider.noUiSlider.on('slide.one', () => this._updatePriceSliderValues())
        this._nodeSelectors.priceSlider.noUiSlider.on('end.one', () => {
            let filterType
            const includePriceCheck = true

            if (this._combineActiveFilters(includePriceCheck).length === 1) {
                filterType = 'single'
                this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), this._checkCurrentSorting(), 1, filterType)
            } else {
                filterType = 'multi'
                this._runFilteredSearch(this._combineActiveFilters(includePriceCheck), this._checkCurrentSorting(), 1, filterType)
            }
        })
    }

    /**
     * Update price slider values.
     */
    _updatePriceSliderValues () {
        this._nodeSelectors.minPriceRange.innerHTML = formatNumber(this._getPriceSliderValues()[0])
        this._nodeSelectors.maxPriceRange.innerHTML = formatNumber(this._getPriceSliderValues()[1])
    }

    /**
     * Get price slider values.
     */
    _getPriceSliderValues () {
        const priceSliderData = this._nodeSelectors.priceSlider.noUiSlider.get()
        return [Object.values(priceSliderData)[0], Object.values(priceSliderData)[1]]
    }

    /**
     * Toggles filters visibility on mobile.
     * @param {HTMLElement} event - Html element.
     */
    _filterToggle (event) {
        event.currentTarget.classList.toggle(cssClasses.active)

        if (event.currentTarget.innerHTML.trim() === window.theme.strings.search.landing.refineBy) {
            event.currentTarget.innerHTML = window.theme.strings.search.landing.hideFilters
        } else {
            event.currentTarget.innerHTML = window.theme.strings.search.landing.refineBy
        }
    }

    /**
     * Activate new grid layout.
     * @param {String} currentGrid - Current grid class.
     * @param {String} newGrid - New grid class.
     */
    _activateNewGrid (currentGrid, newGrid) {
        this._nodeSelectors.klevuLandingContainer.classList.remove(currentGrid)
        this._nodeSelectors.klevuLandingContainer.classList.add(newGrid)
    }

    /**
     * Checks current sorting type.
     */
    _checkCurrentSorting () {
        return this._nodeSelectors.klevuSortLabel.dataset.value
    }

    /**
     * Combines all applied filters into an object.
     * @param {Boolean} includePriceCheck - Indicates if price check is required.
     * @returns {Object} - of all applied filters.
     */
    _combineActiveFilters (includePriceCheck) {
        const appliedFiltersArray = []
        const activeFilters = this._nodeSelectors.klevuFilters.querySelectorAll(this._selectors.klevuActiveFilters)

        activeFilters.forEach((filter) => {
            const filterKey = filter.getAttribute(this._selectors.klevuFilterKey)
            const filterValue = filter.getAttribute(this._selectors.klevuFilterValue)
            const indexInGroupedArray = appliedFiltersArray.findIndex((item) => item.key === filterKey)

            if (indexInGroupedArray === -1) {
                appliedFiltersArray.push({
                    key: filterKey,
                    values: [filterValue],
                    settings: {
                        singleSelect: false
                    }
                })
            } else {
                appliedFiltersArray[indexInGroupedArray].values.push(filterValue)
            }
        })

        if (includePriceCheck) {
            appliedFiltersArray.push({
                key: 'klevu_price',
                values: this._getPriceSliderValues(),
                settings: {
                    singleSelect: false
                }
            })
        }

        return appliedFiltersArray
    }

    /**
     * Run a filtered search query.
     * @param {Object} appliedFiltersArray - Applied filters object.
     * @param {String} appliedSorting - Active sorting type.
     * @param {String} pageNumber - Active page number.
     * @param {String} filterType - Filter type.
     */
    _runFilteredSearch (appliedFiltersArray, appliedSorting, pageNumber, filterType) {
        axios({
            method: 'post',
            url: this._klevuSearchEndpoint,
            data: {
                context: {
                    apiKeys: [
                        this._klevuSearchApiKey
                    ]
                },
                recordQueries: [{
                    id: 'productSearch',
                    typeOfRequest: 'SEARCH',
                    settings: {
                        query: {
                            term: this._incomingSearchTerm
                        },
                        limit: 200,
                        typeOfRecords: ['KLEVU_PRODUCT'],
                        fields: ['id', 'startPrice', 'price', 'sku', 'url', 'image', 'name', 'salePrice', 'tags'],
                        typeOfSearch: 'DEFAULT',
                        sort: appliedSorting
                    },
                    filters: {
                        filtersToReturn: {
                            enabled: true,
                            rangeFilterSettings: [{
                                key: 'klevu_price',
                                minMax: true
                            } ],
                            options: {
                                limit: 80
                            }
                        },
                        applyFilters: {
                            filters: appliedFiltersArray
                        }
                    }
                } ]
            }
        })
            .then(({
                data
            }) => {
                if (data.queryResults[0].meta.totalResultsFound > 0) {
                    this._deactivateNoResults()
                }

                if (data.queryResults[0].meta.typeOfSearch !== 'WILDCARD_AND') {
                    this._showFuzzyResultsMessage()
                }

                if (filterType === 'filter') {
                    if (this._checkForEqualPriceValues(data.queryResults[0].filters)) {
                        this._nodeSelectors.priceSlider.setAttribute('disabled', true)
                        this._nodeSelectors.priceSliderContainer.classList.add(cssClasses.hidden)
                    } else {
                        this._nodeSelectors.priceSlider.removeAttribute('disabled')
                        this._nodeSelectors.priceSliderContainer.classList.remove(cssClasses.hidden)
                        this._updateFilteredPriceRange(data.queryResults[0].filters)
                        this._updatePriceSliderValues()
                    }
                }

                this._nodeSelectors.klevuFilters.innerHTML = this._renderSearchFilters(data.queryResults[0].filters)
                const testM = this._nodeSelectors.klevuFilters.querySelectorAll('[js-klevu-filter-link]')

                testM.forEach((item) => {
                    if (this._activeFiltersArray.findIndex((el) => el.value === item.dataset.value) !== -1) {
                        item.classList.add(cssClasses.active)
                    }
                })

                this._constructAppliedFiltersList()

                if (filterType === 'single') {
                    this._nodeSelectors.klevuFilters.innerHTML = this._renderSearchFilters(data.queryResults[0].filters)
                }

                const slicedResults = this._sliceRecordsArray(data.queryResults[0].records, this._totalProductsPerPage, pageNumber)
                this._buildPagination(pageNumber, data.queryResults[0].records.length)
                this._updateTotalProductCount(data.queryResults[0].meta.totalResultsFound)
                this._nodeSelectors.klevuProductGrid.innerHTML = this._renderSearchResults(slicedResults)
                return data
            })
            .catch((error) => {
                throw new Error('Could not load data', error)
            })
    }

    /**
     * Constructs a list of applied filters.
     */
    _constructAppliedFiltersList () {
        let html = ''
        let visibleList = ''

        if (this._activeFiltersArray.length < 1) {
            visibleList = cssClasses.hidden
        }

        this._activeFiltersArray.forEach((item) => {
            html += `
          <li>
            <button
              class="klevu-search-landing__active-filter"
              data-key="${item.key}"
              data-value="${item.value}"
              js-applied-filter
            >
              ${this._emitActiveFilterTitle(item)}
            </button>
          </li>
        `
        })

        this._nodeSelectors.klevuAppliedFiltersList.innerHTML = `
        <div class="klevu-search-landing__active-filters-inner ${visibleList}">
          <div class="klevu-search-landing__active-filters">
            <h4
              class="klevu-search-landing__active-filters-title"
            >
              ${window.theme.strings.search.landing.refineBy}
            </h4>

            <button
              class="klevu-search-landing__active-filters-clear"
              js-klevu-clear
            >
              ${window.theme.strings.search.landing.clearAll}
            </button>
          </div>

          <ul class="klevu-search-landing__active-filters-list">
            ${html}
          </ul>
        </div>
      `
    }

    /**
     * Constructs applied filter title.
     * @param {Object} item - search query filters.
     * @returns {String} filter title.
     */
    _emitActiveFilterTitle (item) {
        let title
        let currentCar = Cookies.get('Current_Car')
        if (currentCar && item.key === 'Filter_VehicleCode') {
            currentCar = JSON.parse(currentCar)
            title = `${item.key.replace('Filter_VehicleCode', 'Model')}: ${currentCar.model}`
        } else {
            title = `${item.key.replace('Filter_Model', 'Model')}: ${item.value.replace('_', ' ')}`
        }

        return title
    }

    /**
     * Checks for equal min and max price values.
     * @param {Object} filters - search query filters.
     */
    _checkForEqualPriceValues (filters) {
        let equalFilters = false

        filters.forEach((filter) => {
            if (filter.key === 'klevu_price' && filter.min === filter.max) {
                equalFilters = true
            }
        })

        return equalFilters
    }

    /**
     * Updates price range slider with new values.
     * @param {Object} filters - Search query filters.
     */
    _updateFilteredPriceRange (filters) {
        filters.forEach((filter) => {
            if (filter.key !== 'klevu_price') {
                return
            }

            if (this._nodeSelectors.priceSlider.innerHTML === '') {
                noUiSlider.create(this._nodeSelectors.priceSlider, {
                    start: [Number(filter.min), Number(filter.max)],
                    connect: true,
                    range: {
                        min: Number(filter.min),
                        max: Number(filter.max)
                    },
                    pips: {
                        mode: 'positions',
                        values: [0, 25, 50, 75, 100],
                        density: 4
                    }
                })
            }

            this._nodeSelectors.priceSlider.noUiSlider.updateOptions({
                start: [Number(filter.min), Number(filter.max)],
                range: {
                    min: Number(filter.min),
                    max: Number(filter.max)
                }
            })
        })
    }

    /**
     * Slices search query results for pagination.
     * @param {Array} array - Search query results.
     * @param {String} pageSize - Total results per page.
     * @param {String} pageNumber - Active pagination page number.
     */
    _sliceRecordsArray (array, pageSize, pageNumber) {
        return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
    }

    /**
     * Builds pagination template.
     * @param {Object} activePage - Active pagination page number.
     * @param {Object} totalRecord - Total search records.
     */
    _buildPagination (activePage, totalRecord) {
        let html = ''
        const requiredPages = Math.ceil(totalRecord / this._totalProductsPerPage)
        const paginationObject = this._pagination(activePage, requiredPages)

        paginationObject.forEach((item) => {
            let activeClass = ''

            if (item === activePage) {
                activeClass = cssClasses.active
            }

            if (item === '...') {
                html += `
            <li class="klevu-search-landing__pagination-item">${item}</li>
          `
            } else {
                html += `
            <li class="klevu-search-landing__pagination-item">
              <button
                class="klevu-search-landing__pagination-link ${activeClass}"
                data-page="${item}"
                js-klevu-pagination
              >
                ${item}
              </button>
            </li>
          `
            }
        })

        this._nodeSelectors.klevuPaginationContainer.innerHTML = html
    }

    /**
     * Builds pagination items.
     * @param {Object} currentPage - Current pagination number.
     * @param {Object} lastPage - Last pagination number.
     */
    _pagination (currentPage, lastPage) {
        const current = currentPage
        const last = lastPage
        const delta = 2
        const left = current - delta
        const right = current + delta + 1
        const range = []
        const rangeWithDots = []
        let newRange

        for (let i = 1; i <= last; i++) {
            if (i === 1 || i === last || (i >= left && i < right)) {
                range.push(i)
            }
        }

        for (const i of range) {
            if (newRange) {
                if (i - newRange === 2) {
                    rangeWithDots.push(newRange + 1)
                } else if (i - newRange !== 1) {
                    rangeWithDots.push('...')
                }
            }

            rangeWithDots.push(i)
            newRange = i
        }

        return rangeWithDots
    }

    // /**
    //  * Expose public interface.
    //  */
    // return Object.freeze({
    //   init,
    // })
}

export default KlevuLanding



// WEBPACK FOOTER //
// ./src/resources/scripts/components/KlevuLanding.js